import type { ReactNode } from "react";
import { Breadcrumb, type BreadcrumbItem } from "@/components/ui/breadcrumb";
import { AskAiButton } from "@/components/assistant/assistant-provider";
import {
  PageHeaderActionsSlot,
  usePageHeaderActionsContext,
} from "@/components/ui/page-header-actions";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode;
  askAi?: boolean;
  className?: string;
  // Hide on mobile — the MobileTopBar already shows the title there.
  // Default true; opt out when a page also wants the header visible on mobile
  // (e.g. a list page where desc/actions carry meaning beyond the title).
  hideTitleOnMobile?: boolean;
}

export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
  askAi = false,
  className,
  hideTitleOnMobile = true,
}: PageHeaderProps) {
  const headerActions = usePageHeaderActionsContext();
  const hasActions = askAi || Boolean(actions) || headerActions !== null;

  return (
    <div className={cn("flex flex-col gap-2 mb-4", className)}>
      {breadcrumb && breadcrumb.length > 0 && (
        <Breadcrumb items={breadcrumb} />
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1
            className={cn(
              "text-2xl font-semibold tracking-tight truncate",
              hideTitleOnMobile && "hidden lg:block"
            )}
          >
            {title}
          </h1>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        {hasActions && (
          <div className="flex items-center gap-2 shrink-0">
            {askAi && <AskAiButton />}
            {actions}
            <PageHeaderActionsSlot />
          </div>
        )}
      </div>
    </div>
  );
}
